import { createSelector } from "reselect";
import AppState from "../AppState";
import { getCategories } from "../category/CategorySelectors";
import { ColorProduct, Product } from "../product/ProductTypes";
import { selectProductFetched } from "../product/ProductSelectors";
import { calculateV1Progress } from "../../utils/ProductUtil";

export const selectFindProductsState = (state: AppState) => {
  return state.findProducts;
};

export const selectTopCategoriesFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.topCategory;
  },
);

export const selectChildCategoriesFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.childCategory;
  },
);

export const selectCategoriesFromFindProducts = createSelector(
  [selectTopCategoriesFromFindProducts, selectChildCategoriesFromFindProducts],
  (catArray, childCategory) => {
    return getCategories(catArray, childCategory, null, []);
  },
);

export const selectProductsFromFindProducts = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.products || [];
  },
);

export const selectProductsSizeFromFindProducts = createSelector(
  [selectProductsFromFindProducts],
  (products) => {
    return products.length;
  },
);

export const selectIsSubmitting = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.isSubmitting;
  },
);

export const selectIsSubmitted = createSelector(
  [selectFindProductsState],
  (findProducts) => {
    return findProducts.isSubmitted;
  },
);

export const selectTopCategoriesForFindProductsModalBasedOnCatalog = (
  state: AppState,
) => {
  return state.category.findProductsModalTopCategory;
};

export const selectChildCategoriesBasedOnCategorySelectionForFindProducts = (
  state: AppState,
) => {
  return state.category.findProductsModalChildCategory;
};

export const selectCategoriesForFindProducts = createSelector(
  [
    selectTopCategoriesForFindProductsModalBasedOnCatalog,
    selectChildCategoriesBasedOnCategorySelectionForFindProducts,
  ],
  (catArray, childCategory) => {
    return getCategories(catArray, childCategory, null, []);
  },
);

export const selectGetProductListForFindProductsModal = (state: AppState) => {
  return state.findProducts.productList;
};

export const selectAreAllPagesFetched = (state: AppState) => {
  return state.findProducts && state.findProducts.allPagesFetched;
};

export const selectGetProductIds = (state: AppState) => {
  return state.findProducts && state.findProducts.productIds;
};

export const selectAddingRemovingProductIdsList = (state: AppState) => {
  return state.findProducts && state.findProducts.addingRemovingProductIdsList;
};

export const selectProductByIdForFindProducts = (state: AppState) => {
  return state.findProducts && state.findProducts.productById;
};

export const selectProductsColorDataForFindProducts = (state: AppState) => {
  return state.findProducts?.productsColorData ?? {};
};

export const selectIsFetchingProductIds = (state: AppState) => {
  return state.findProducts && state.findProducts.isFetchingProductIds;
};

export const selectIsProductIdsFetched = (state: AppState) => {
  return state.findProducts && state.findProducts.isProductIdsFetched;
};

/**
 * Merge find-products `productById` with `productsColorData` (modal color fetch).
 */
export const selectFindProductsMergedProductRow = (
  state: AppState,
  productId: string,
): Product | undefined => {
  const product = state.findProducts?.productById?.[productId];
  const productColorData = state.findProducts?.productsColorData?.[
    productId
  ] as ColorProduct | undefined;
  if (!product && !productColorData) return undefined;
  const row = (product ?? {}) as Product;
  const colors =
    row.colors && row.colors.length > 0
      ? row.colors
      : (productColorData?.colors ?? []);
  const mergedProduct = {
    ...row,
    productId: row.productId || productId,
    colors,
    hasColors: row.hasColors ?? Boolean(colors.length),
    hasManageableColors:
      row.hasManageableColors ??
      Boolean(productColorData?.hasManageableColors ?? false),
    hasSequenceableColors:
      row.hasSequenceableColors ??
      Boolean(productColorData?.hasSequenceableColors ?? false),
    colorVariationAttributeId:
      row.colorVariationAttributeId ||
      productColorData?.colorVariationAttributeId ||
      "",
    categorySpecific:
      row.categorySpecific ?? productColorData?.categorySpecific ?? [],
  } as Product;
  return mergedProduct.productId ? mergedProduct : undefined;
};

/**
 * Row for Find Products UI + modals opened from it: find-products data wins over main
 * `products` when both exist so search/category do not depend on the grid cache.
 * Falls back to `selectProductFetched` when find has no row (e.g. grid-only open).
 */
export const selectDisplayProductForFindProductsModal = (
  state: AppState,
  productId: string,
): Product | undefined => {
  const findProduct = selectFindProductsMergedProductRow(state, productId);
  const gridProduct = selectProductFetched(state, productId);
  if (!findProduct && !gridProduct) return undefined;
  if (!gridProduct) return findProduct;
  if (!findProduct) return gridProduct;
  const colors =
    findProduct.colors && findProduct.colors.length > 0
      ? findProduct.colors
      : (gridProduct.colors ?? []);
  return {
    ...gridProduct,
    ...findProduct,
    colors,
  };
};

const FIND_PRODUCTS_PROGRESS_CACHE_KEY = "find-products";

/** Determinate 0–100 loading % for Find Products (query + category): mirrors main grid staging (IDs/query ~15%, details+colors ~85%). */
export const selectFindProductsLoadingPercentage = (
  state: AppState,
): number => {
  const fp = state.findProducts;
  const productIds = fp.productIds ?? [];
  const N = productIds.length;
  const productById = fp.productById ?? {};
  const productsColorData = fp.productsColorData ?? {};
  const isFetchingProductIds = Boolean(fp.isFetchingProductIds);
  const isProductIdsFetched = Boolean(fp.isProductIdsFetched);
  const allPagesFetched = Boolean(fp.allPagesFetched);
  const progressCache = fp.progressCache ?? {};
  const queryPagesCompleted = fp.queryPagesCompleted ?? 0;

  if (
    N === 0 &&
    !isFetchingProductIds &&
    isProductIdsFetched &&
    allPagesFetched
  ) {
    return 100;
  }

  if (
    N === 0 &&
    !isFetchingProductIds &&
    !isProductIdsFetched &&
    !allPagesFetched
  ) {
    return 0;
  }

  const detailLoaded = (p: Product | undefined): boolean => {
    if (!p?.productId) return false;
    if (p.productDetailsLoaded) return true;
    return (
      p.listPrice !== undefined ||
      p.lowPrice !== undefined ||
      p.highPrice !== undefined
    );
  };

  if (N === 0 && isFetchingProductIds) {
    const raw = Math.min(12, 5 + queryPagesCompleted * 2);
    return calculateV1Progress(
      100,
      raw,
      FIND_PRODUCTS_PROGRESS_CACHE_KEY,
      progressCache,
    );
  }

  if (isFetchingProductIds && N > 0) {
    const raw = Math.min(15, 4 + queryPagesCompleted * 3);
    return calculateV1Progress(
      100,
      raw,
      FIND_PRODUCTS_PROGRESS_CACHE_KEY,
      progressCache,
    );
  }

  if (N === 0) {
    return calculateV1Progress(
      100,
      100,
      FIND_PRODUCTS_PROGRESS_CACHE_KEY,
      progressCache,
    );
  }

  let totalItems = 0;
  let loadedItems = 0;
  for (const pid of productIds) {
    const p = productById[pid];
    totalItems += 1;
    if (!detailLoaded(p)) {
      continue;
    }
    loadedItems += 1;
    if (p?.hasColors) {
      totalItems += 1;
      if (productsColorData[pid]?.productColorDetailsLoaded) {
        loadedItems += 1;
      }
    }
  }

  const innerPct =
    totalItems > 0
      ? Math.min(100, Math.floor((loadedItems / totalItems) * 100))
      : 100;

  const idsProgress = 15;
  const combined =
    innerPct >= 100
      ? 100
      : Math.min(100, idsProgress + Math.floor((innerPct * 85) / 100));

  return calculateV1Progress(
    100,
    combined,
    FIND_PRODUCTS_PROGRESS_CACHE_KEY,
    progressCache,
  );
};
